import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import ReactECharts from 'echarts-for-react';
import { getKbCompareChart, getKbDataChartMulti, getKbRegions, getKbPivotTable, getKbBuyerSentimentTable, getSentimentTimingConfig } from '../lib/api/kb-api';
import './KbRegionCompare.css';

// KB지수-표 색상 규칙 그대로 유지 (전 주 대비 증감 음영)
const getWeeklyChangeClassByDelta = (delta, isWeeklyChangeTable) => {
  if (!isWeeklyChangeTable || delta === null || delta === undefined) return '';
  const num = Number(delta);
  if (Number.isNaN(num)) return '';
  const abs = Math.abs(num);
  if (num >= 0.8) return 'cell-rise-4';
  if (num >= 0.6) return 'cell-rise-3';
  if (num >= 0.4) return 'cell-rise-2';
  if (num >= 0.2) return 'cell-rise-1';
  if (num <= -0.8) return 'cell-fall-4';
  if (num <= -0.6) return 'cell-fall-3';
  if (num <= -0.4) return 'cell-fall-2';
  if (num <= -0.2) return 'cell-fall-1';
  if (abs < 0.2) return 'cell-neutral';
  return '';
};

// 매수매도심리 타이밍 색상 기준.
//  매도세=매도자많음(seller), 매수세=매수자많음(buyer), 매수우위=매수우위지수(index)
//  기준은 백엔드(kb_sentiment_timing_config)에서 로드하며, 없으면 아래 기본값(DEFAULT_TIMING_CONFIG) 사용.
//  평가 우선순위 = 배열 순서(먼저 매칭되는 색 적용). 타이밍을 대기보다 먼저.
const DEFAULT_TIMING_CONFIG = [
  { category: 'BUY_TIMING', label: '매수타이밍', color: '#FFC7CE', sellerOp: '>=', sellerValue: 80, buyerOp: '<=', buyerValue: 1.5, indexOp: '<=', indexValue: 20, sortOrder: 1 },
  { category: 'BUY_WAIT', label: '매수대기', color: '#FFE181', sellerOp: '>=', sellerValue: 70, buyerOp: '<=', buyerValue: 3, indexOp: '<=', indexValue: 30, sortOrder: 2 },
  { category: 'SELL_TIMING', label: '매도타이밍', color: '#C9C9FF', sellerOp: '<=', sellerValue: 20, buyerOp: '>=', buyerValue: 20, indexOp: '>=', indexValue: 100, sortOrder: 3 },
  { category: 'SELL_WAIT', label: '매도대기', color: '#A7FFCF', sellerOp: '<=', sellerValue: 30, buyerOp: '>=', buyerValue: 10, indexOp: '>=', indexValue: 80, sortOrder: 4 },
];

const compareOp = (val, op, threshold) => {
  const t = Number(threshold);
  if (!Number.isFinite(t)) return false;
  switch (op) {
    case '>=': return val >= t;
    case '<=': return val <= t;
    case '>': return val > t;
    case '<': return val < t;
    case '=':
    case '==': return val === t;
    default: return false;
  }
};

// configs: 백엔드 기준 목록(빈 배열이면 기본값). 순서대로 첫 매칭 색 반환.
const getSentimentTimingColor = (seller, buyer, index, configs) => {
  const s = Number(seller);
  const b = Number(buyer);
  const i = Number(index);
  if (!Number.isFinite(s) || !Number.isFinite(b) || !Number.isFinite(i)) return null;
  const list = (Array.isArray(configs) && configs.length > 0) ? configs : DEFAULT_TIMING_CONFIG;
  for (const c of list) {
    if (compareOp(s, c.sellerOp, c.sellerValue)
      && compareOp(b, c.buyerOp, c.buyerValue)
      && compareOp(i, c.indexOp, c.indexValue)) {
      return c.color;
    }
  }
  return null;
};


const KB_REGIONS = [
  { regionId: 735, name: '전국', defaultChecked: true },
  { regionId: 736, name: '서울', defaultChecked: false },
  { regionId: 737, name: '강북14개구', defaultChecked: false },
  { regionId: 738, name: '강남11개구', defaultChecked: false },
  { regionId: 739, name: '수도권', defaultChecked: true },
  { regionId: 740, name: '6개광역시', defaultChecked: false },
  { regionId: 741, name: '5개광역시', defaultChecked: true },
  { regionId: 742, name: '기타지방', defaultChecked: true },
  { regionId: 743, name: '부산', defaultChecked: false },
  { regionId: 745, name: '대구', defaultChecked: false },
  { regionId: 746, name: '인천', defaultChecked: false },
  { regionId: 747, name: '(구)광주광역시', defaultChecked: false },
  { regionId: 748, name: '대전', defaultChecked: false },
  { regionId: 749, name: '울산', defaultChecked: false },
  { regionId: 750, name: '세종', defaultChecked: false },
  { regionId: 751, name: '경기', defaultChecked: false },
  { regionId: 752, name: '충북', defaultChecked: false },
  { regionId: 753, name: '충남', defaultChecked: false },
  { regionId: 754, name: '(구)전라남도', defaultChecked: false },
  { regionId: 755, name: '경북', defaultChecked: false },
  { regionId: 756, name: '경남', defaultChecked: false },
  { regionId: 757, name: '제주', defaultChecked: false },
  { regionId: 758, name: '강원', defaultChecked: false },
  { regionId: 759, name: '전북', defaultChecked: false }
];

const INDEX_TYPES = [
  { value: 'SALE_IDX', label: '매매지수' },
  { value: 'JEONSE_IDX', label: '전세지수' },
  { value: 'BUYER_SENTIMENT', label: '매수심리', isSentiment: true }
];

const INDEX_CHANGE_MAP = {
  SALE_IDX: 'SALE',
  JEONSE_IDX: 'JEONSE'
};

const CHART_COLORS = [
  '#1f77b4', '#ff7f0e', '#2ca02c', '#d62728', '#9467bd',
  '#8c564b', '#e377c2', '#7f7f7f', '#bcbd22', '#17becf',
  '#aec7e8', '#ffbb78', '#98df8a', '#ff9896', '#c5b0d5',
  '#c49c94', '#f7b6d2', '#c7c7c7', '#dbdb8d', '#9edae5'
];

const getSeriesEndLabel = (name, color) => ({
  show: true,
  formatter: () => name,
  color,
  fontSize: 12,
  fontWeight: 600,
  padding: [2, 6],
  backgroundColor: 'rgba(255,255,255,0.9)',
  borderColor: color,
  borderWidth: 1,
  borderRadius: 4
});

const MAIN_CHART_MODES = [
  { value: 'INDEX', label: '지수' },
  { value: 'CHANGE', label: '증감' }
];

const BUYER_SENTIMENT_METRICS = [
  { value: 'ALL', label: '전체', key: null, color: null },
  { value: 'SELLER', label: '매도자 많음', key: 'seller', color: '#f59e0b' },
  { value: 'BUYER', label: '매수자 많음', key: 'buyer', color: '#3b82f6' },
  { value: 'INDEX', label: '매수우위지수', key: 'index', color: '#10b981' }
];

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return dateStr;
  const yy = String(date.getFullYear()).slice(2);
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  return `'${yy}.${mm}`;
};

// 연월일까지 표기 (매수심리 차트 x축용)
const formatDateFull = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return dateStr;
  const yy = String(date.getFullYear()).slice(2);
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  return `'${yy}.${mm}.${dd}`;
};

// 서버 재요청용 날짜 유틸
const FULL_START_DATE = '2008-01-01';   // KB 지수 데이터 시작 시점
const DEFAULT_LOOKBACK_YEARS = 5;       // 기본 최근 5년
const MIN_ZOOM_SPAN_MS = 90 * 24 * 60 * 60 * 1000; // 줌인 최소 구간(약 3개월)
const DEFAULT_VIEW_YEARS = 3;           // 그룹 차트 기본 표시기간(최근 3년)
const FULL_VIEW_YEARS = 100;            // '전체' — 사실상 전체 범위

const toDateStr = (date) => {
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return null;
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const getVolumeBarStyle = () => ({
  color: {
    type: 'linear',
    x: 0,
    y: 0,
    x2: 0,
    y2: 1,
    colorStops: [
      { offset: 0, color: 'rgba(235, 87, 87, 0.95)' },
      { offset: 1, color: 'rgba(235, 87, 87, 0.35)' }
    ]
  },
  borderRadius: [2, 2, 0, 0]
});

function KbRegionCompare() {
  const [selectedRegions, setSelectedRegions] = useState(
    KB_REGIONS.filter(r => r.defaultChecked).map(r => r.regionId)
  );
  // 지역 목록은 kb_region API(getKbRegions)에서 로드, 실패 시 하드코딩(KB_REGIONS) 유지
  const [regions, setRegions] = useState(KB_REGIONS);
  // 매수심리 타이밍 색상 기준 (백엔드 config → 없으면 기본값)
  const [timingConfig, setTimingConfig] = useState(DEFAULT_TIMING_CONFIG);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await getSentimentTimingConfig();
        const list = Array.isArray(res) ? res : (res?.data ?? res?.items ?? []);
        if (alive && Array.isArray(list) && list.length > 0) setTimingConfig(list);
      } catch { /* 실패 시 기본값 유지 */ }
    })();
    return () => { alive = false; };
  }, []);
  // 트리 펼침 상태 (regionId -> bool). 기본 접힘(최상위만 노출)
  const [expandedRegions, setExpandedRegions] = useState({});
  const [currentIndexType, setCurrentIndexType] = useState('SALE_IDX');
  const [mainChartMode, setMainChartMode] = useState('INDEX');
  const [buyerSentimentMetric, setBuyerSentimentMetric] = useState('ALL');
  const [zoomSpanPercent, setZoomSpanPercent] = useState(100);
  // 그룹 차트 공통 표시기간(년). 데이터는 전체 로드하고 화면엔 이 기간(dataZoom 창)만 보인다. 기본 3년.
  const [viewYears, setViewYears] = useState(DEFAULT_VIEW_YEARS);
  // 차트별 수동 줌 창(Ctrl+휠 확대/축소·드래그 이동) — { ancestorId: { startValue, endValue(ms) } }
  const [groupZoomByKey, setGroupZoomByKey] = useState({});
  const groupChartRefs = useRef({});
  const groupZoomTimerRef = useRef(null);
  const [chartData, setChartData] = useState({ index: null, change: null });
  const [sentimentData, setSentimentData] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // 차트 하단 KB지수-표 데이터 (선택 지역 + 현재 지수/증감 모드에 연동)
  const [tableData, setTableData] = useState(null);
  const [tableLoading, setTableLoading] = useState(false);
  // 메인 지수/증감 차트의 서버 조회 구간 (null이면 백엔드 기본 최근 5년)
  const [mainRange, setMainRange] = useState({ startDate: null, endDate: null });
  const mainChartRef = useRef(null);
  const zoomDebounceRef = useRef(null);
  const suppressZoomRef = useRef(false);

  // kb_region API 에서 지역 목록 로드 (regionId + 한글명). 실패 시 하드코딩 fallback 유지.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await getKbRegions();
        const items = Array.isArray(res?.items) ? res.items : (Array.isArray(res) ? res : []);
        const mapped = items
          .map((it) => ({
            regionId: Number(it?.regionId ?? it?.region_id),
            name: String(it?.regionNameKo ?? it?.region_name_ko ?? '').trim(),
            parentId: (it?.parentId ?? it?.parent_id) != null ? Number(it.parentId ?? it.parent_id) : null,
            sortOrder: (it?.sortOrder ?? it?.sort_order) != null ? Number(it.sortOrder ?? it.sort_order) : null,
          }))
          .filter((r) => Number.isFinite(r.regionId) && r.regionId > 0 && r.name);
        if (alive && mapped.length > 0) setRegions(mapped);
      } catch (e) {
        // 하드코딩 KB_REGIONS 유지
      }
    })();
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    setGroupZoomByKey({}); // 새 데이터 로드 시 차트별 수동 줌 초기화(기본 표시기간 창으로)
    if (selectedRegions.length > 0) {
      fetchChartData();
    } else {
      // 전체해제 시 차트 비우기
      setChartData({ index: null, change: null });
      setSentimentData({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRegions, currentIndexType, mainRange]);

  // 차트 하단 표: 차트가 보여주는 뷰(지수/증감/매수심리)에 맞춰 조회
  // - 증감(CHANGE) 뷰일 때만 SALE/JEONSE → KB지수-표 색상 규칙 적용됨
  const tableIndexType = useMemo(() => {
    if (currentIndexType === 'BUYER_SENTIMENT') return 'BUYER_SELLER';
    if (mainChartMode === 'CHANGE') return INDEX_CHANGE_MAP[currentIndexType] || currentIndexType;
    return currentIndexType; // SALE_IDX / JEONSE_IDX
  }, [currentIndexType, mainChartMode]);

  useEffect(() => {
    if (selectedRegions.length === 0) {
      setTableData(null);
      return;
    }
    let alive = true;
    (async () => {
      try {
        setTableLoading(true);
        // 표는 최근 1년만
        const oneYearAgo = (() => { const d = new Date(); d.setFullYear(d.getFullYear() - 1); return toDateStr(d); })();
        const res = tableIndexType === 'BUYER_SELLER'
          ? await getKbBuyerSentimentTable({ regionIds: selectedRegions, startDate: oneYearAgo })
          : await getKbPivotTable({ indexType: tableIndexType, regionIds: selectedRegions, sortOrder: 'DESC', startDate: oneYearAgo });
        if (alive) setTableData(res ? { ...res, _indexType: tableIndexType } : null);
      } catch (e) {
        if (alive) setTableData(null);
      } finally {
        if (alive) setTableLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [selectedRegions, tableIndexType]);

  const fetchChartData = async () => {
    try {
      setLoading(true);
      setError(null);

      if (currentIndexType === 'BUYER_SENTIMENT') {
        // 매수심리 데이터 가져오기
        const sentimentByRegion = {};
        const sentimentList = await Promise.all(selectedRegions.map(async (regionId) => {
          const data = await getKbDataChartMulti({
            regionId,
            indexTypes: ['BUYER_SE_SELLER', 'BUYER_SE_BUYER', 'BUYER_SE_INDEX'],
            lookbackYears: 20
          });
          return { regionId, data };
        }));
        sentimentList.forEach(({ regionId, data }) => {
          sentimentByRegion[regionId] = data;
        });
        setSentimentData(sentimentByRegion);
        setChartData({ index: null, change: null });
      } else {
        // 일반 지수 + 증감 데이터 함께 가져오기
        // 데이터는 전체 기간(2008~)을 모두 로드하고, 화면에는 dataZoom 창(기본 3년)만 표시한다.
        const rangeParams = { startDate: FULL_START_DATE, endDate: toDateStr(new Date()) };
        const changeType = INDEX_CHANGE_MAP[currentIndexType];
        const [indexData, changeData] = await Promise.all([
          getKbCompareChart({
            regionIds: selectedRegions,
            indexType: currentIndexType,
            ...rangeParams
          }),
          changeType
            ? getKbCompareChart({
              regionIds: selectedRegions,
              indexType: changeType,
              ...rangeParams
            })
            : Promise.resolve(null)
        ]);

        setChartData({
          index: indexData,
          change: changeData
        });
        setSentimentData({});
      }
    } catch (err) {
      console.error('KB 비교 차트 조회 오류:', err);
      setError('데이터를 불러오는 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
      // 새 데이터로 옵션이 재렌더되며 dataZoom이 리셋될 때 자동 재요청되는 것을 방지
      suppressZoomRef.current = true;
      setTimeout(() => { suppressZoomRef.current = false; }, 500);
    }
  };

  const handleSelectAllRegions = () => setSelectedRegions(regions.map((r) => r.regionId));
  const handleDeselectAllRegions = () => setSelectedRegions([]);

  const openSubChartPopup = (regionId, option) => {
    if (!option) return;

    const regionName = regions.find((r) => r.regionId === regionId)?.name || String(regionId);
    const popupPayload = {
      title: `${regionName} 차트`,
      option
    };
    const key = `kb-subchart-popup-${regionId}-${Date.now()}`;
    sessionStorage.setItem(key, JSON.stringify(popupPayload));

    const popupWidth = 1100;
    const popupHeight = 470;
    const left = Math.max(0, Math.round((window.screen.width - popupWidth) / 2));
    const top = Math.max(0, Math.round((window.screen.height - popupHeight) / 2));
    const url = `/compare/sub-chart-popup?key=${encodeURIComponent(key)}`;
    const features = `width=${popupWidth},height=${popupHeight},left=${left},top=${top},toolbar=no,menubar=no,location=no,status=no,scrollbars=no,resizable=yes`;

    window.open(url, `kb-subchart-${regionId}`, features);
  };

  // 현재 적용 중인 조회 구간(없으면 기본 최근 5년)을 Date로 환산
  const getEffectiveRange = () => {
    const end = mainRange.endDate ? new Date(mainRange.endDate) : new Date();
    let start;
    if (mainRange.startDate) {
      start = new Date(mainRange.startDate);
    } else {
      start = new Date(end);
      start.setFullYear(start.getFullYear() - DEFAULT_LOOKBACK_YEARS);
    }
    return { start, end };
  };

  // 구간을 백엔드에 재요청 (FULL_START_DATE 이전으로는 확장하지 않음)
  const applyMainRange = (start, end) => {
    const fullStart = new Date(FULL_START_DATE);
    const s = start < fullStart ? fullStart : start;
    if (end <= s) return;
    const nextStart = toDateStr(s);
    const nextEnd = toDateStr(end);
    if (!nextStart || !nextEnd) return;
    if (nextStart === mainRange.startDate && nextEnd === mainRange.endDate) return; // 동일 구간이면 생략
    setMainRange({ startDate: nextStart, endDate: nextEnd });
  };

  const handleZoomIn = () => {
    if (currentIndexType === 'BUYER_SENTIMENT') {
      setZoomSpanPercent((prev) => Math.max(20, prev - 15));
      return;
    }
    const { start, end } = getEffectiveRange();
    const span = end.getTime() - start.getTime();
    const nextSpan = Math.max(MIN_ZOOM_SPAN_MS, span * 0.5);
    applyMainRange(new Date(end.getTime() - nextSpan), end);
  };

  const handleZoomOut = () => {
    if (currentIndexType === 'BUYER_SENTIMENT') {
      setZoomSpanPercent((prev) => Math.min(100, prev + 15));
      return;
    }
    const { start, end } = getEffectiveRange();
    const span = end.getTime() - start.getTime();
    applyMainRange(new Date(start.getTime() - span), end); // 과거로 2배 확장
  };

  const handleZoomReset = () => {
    if (currentIndexType === 'BUYER_SENTIMENT') {
      setZoomSpanPercent(100);
      return;
    }
    // 전체: 데이터 시작(2008)부터 오늘까지
    applyMainRange(new Date(FULL_START_DATE), new Date());
  };

  // 현재 로드된 데이터의 날짜 범위(ms)
  const getLoadedDateExtent = () => {
    const seriesList = chartData?.index?.series || [];
    let min = null;
    let max = null;
    seriesList.forEach((s) => {
      (s.data || []).forEach((point) => {
        const t = new Date(point?.baseDate).getTime();
        if (Number.isNaN(t)) return;
        if (min == null || t < min) min = t;
        if (max == null || t > max) max = t;
      });
    });
    return (min == null || max == null) ? null : { min, max };
  };

  // 마우스휠/드래그 줌 시 보이는 구간을 백엔드에 재요청 (디바운스)
  const handleMainDataZoom = () => {
    if (currentIndexType === 'BUYER_SENTIMENT') return; // 매수심리는 클라이언트 줌 유지
    if (suppressZoomRef.current) return;
    if (zoomDebounceRef.current) clearTimeout(zoomDebounceRef.current);
    zoomDebounceRef.current = setTimeout(() => {
      const inst = mainChartRef.current?.getEchartsInstance?.();
      if (!inst) return;
      const dz = (inst.getOption()?.dataZoom || [])[0];
      if (!dz) return;
      let startValue = dz.startValue;
      let endValue = dz.endValue;
      if (startValue == null || endValue == null) {
        const extent = getLoadedDateExtent();
        if (!extent) return;
        const span = extent.max - extent.min;
        const startPct = typeof dz.start === 'number' ? dz.start : 0;
        const endPct = typeof dz.end === 'number' ? dz.end : 100;
        startValue = extent.min + span * (startPct / 100);
        endValue = extent.min + span * (endPct / 100);
      }
      applyMainRange(new Date(startValue), new Date(endValue));
    }, 500);
  };

  // 전역 표시기간 핸들러 (모든 그룹 차트 공통). 버튼 조작 시 차트별 수동 줌(Ctrl+휠/드래그)은 초기화.
  const handleChartZoomIn = () => { setGroupZoomByKey({}); setViewYears((y) => Math.max(0.5, (y >= FULL_VIEW_YEARS ? 20 : y) / 1.5)); }; // 기간 좁히기
  const handleChartZoomOut = () => { setGroupZoomByKey({}); setViewYears((y) => Math.min(FULL_VIEW_YEARS, (y || 1) * 1.5)); };           // 기간 넓히기
  const handleChartZoomReset = () => { setGroupZoomByKey({}); setViewYears(FULL_VIEW_YEARS); };                                            // 전체

  // 그룹 차트 줌/이동(Ctrl+휠·드래그) 후 현재 창(ms)을 저장 → notMerge 재렌더 후에도 창 유지.
  const captureGroupZoom = (key) => {
    if (groupZoomTimerRef.current) clearTimeout(groupZoomTimerRef.current);
    groupZoomTimerRef.current = setTimeout(() => {
      const inst = groupChartRefs.current[key]?.getEchartsInstance?.();
      if (!inst) return;
      const dz = (inst.getOption()?.dataZoom || [])[0];
      if (!dz || dz.startValue == null || dz.endValue == null) return;
      const sv = new Date(dz.startValue).getTime();
      const ev = new Date(dz.endValue).getTime();
      if (!Number.isFinite(sv) || !Number.isFinite(ev)) return;
      setGroupZoomByKey((prev) => {
        const cur = prev[key];
        if (cur && cur.startValue === sv && cur.endValue === ev) return prev;
        return { ...prev, [key]: { startValue: sv, endValue: ev } };
      });
    }, 160);
  };
  useEffect(() => () => { if (groupZoomTimerRef.current) clearTimeout(groupZoomTimerRef.current); }, []);

  // 차트 위에서 '일반 휠'은 페이지가 스크롤되게 한다.
  //  ECharts(inside dataZoom)가 휠을 가로채 preventDefault 하므로, 캡처 단계에서 먼저 이벤트를
  //  가로채 ECharts로 전파되지 않게 stopPropagation → 브라우저 기본 스크롤이 동작.
  //  Ctrl(⌘) 휠은 그대로 통과시켜 ECharts 줌(zoomOnMouseWheel:'ctrl')이 처리하게 둔다.
  useEffect(() => {
    const onWheelCapture = (e) => {
      if (e.ctrlKey || e.metaKey) return;                  // 줌: ECharts로 통과
      if (!e.target?.closest?.('.kb-individual-chart')) return; // 차트 위에서만
      e.stopPropagation();                                 // ECharts 휠 처리 차단 → 페이지 스크롤
    };
    window.addEventListener('wheel', onWheelCapture, { capture: true, passive: true });
    return () => window.removeEventListener('wheel', onWheelCapture, { capture: true });
  }, []);

  const buildCompareSeriesMap = (response) => {
    const map = new Map();
    (response?.series || []).forEach((series) => {
      map.set(series.regionId, {
        regionId: series.regionId,
        regionNameKo: series.regionNameKo,
        points: series.data?.map((point) => [point.baseDate, point.value]) || []
      });
    });
    return map;
  };

  const chartOption = useMemo(() => {
    const seriesList = [];
    let colorIndex = 0;
    let minIndexDate = null;
    let maxChangeAbs = 0;
    const showIndex = mainChartMode === 'INDEX';
    const showChange = mainChartMode === 'CHANGE';
    const buyerMetric = BUYER_SENTIMENT_METRICS.find((item) => item.value === buyerSentimentMetric) || BUYER_SENTIMENT_METRICS[2];

    if (currentIndexType === 'BUYER_SENTIMENT') {
      // 매수심리 차트
      Object.entries(sentimentData).forEach(([regionId, data]) => {
        if (!Array.isArray(data)) return;

        const regionName = regions.find(r => r.regionId === Number(regionId))?.name || regionId;

        // 날짜별로 그룹화
        const dateMap = new Map();
        data.forEach(row => {
          const baseDate = row?.baseDate;
          const indexType = row?.indexType;
          const value = row?.value;

          if (!baseDate) return;

          if (!dateMap.has(baseDate)) {
            dateMap.set(baseDate, { seller: null, buyer: null, index: null });
          }

          const current = dateMap.get(baseDate);
          if (indexType === 'BUYER_SE_SELLER') current.seller = value;
          if (indexType === 'BUYER_SE_BUYER') current.buyer = value;
          if (indexType === 'BUYER_SE_INDEX') current.index = value;
        });

        const sortedData = Array.from(dateMap.entries())
          .sort((a, b) => new Date(a[0]) - new Date(b[0]));
        const metricPoints = sortedData.map(([date, values]) => {
          const metricValue = values[buyerMetric.key];
          return [date, metricValue, values.seller, values.buyer, values.index];
        });

        const color = CHART_COLORS[colorIndex % CHART_COLORS.length];
        colorIndex++;
        const firstIndexPoint = metricPoints.find((point) => point?.[1] != null);
        if (firstIndexPoint?.[0]) {
          const current = new Date(firstIndexPoint[0]).getTime();
          if (!Number.isNaN(current) && (minIndexDate == null || current < minIndexDate)) {
            minIndexDate = current;
          }
        }

        seriesList.push({
          name: regionName,
          type: 'line',
          smooth: false,
          sampling: 'lttb',
          connectNulls: true,
          symbol: 'none',
          color,
          lineStyle: { width: 2 },
          data: metricPoints,
          z: 3
        });
      });
    } else {
      // 일반 지표 차트
      const indexMap = buildCompareSeriesMap(chartData?.index);
      const changeMap = buildCompareSeriesMap(chartData?.change);

      indexMap.forEach((series, regionId) => {
        const color = CHART_COLORS[colorIndex % CHART_COLORS.length];
        colorIndex++;

        if (showIndex) {
          seriesList.push({
            name: series.regionNameKo,
            type: 'line',
            smooth: false,
            sampling: 'lttb',
            connectNulls: true,
            symbol: 'none',
            color,
            lineStyle: { width: 2 },
            data: series.points || [],
            z: 3
          });
        }
        const firstIndexPoint = (series.points || []).find((point) => point?.[1] != null);
        if (firstIndexPoint?.[0]) {
          const current = new Date(firstIndexPoint[0]).getTime();
          if (!Number.isNaN(current) && (minIndexDate == null || current < minIndexDate)) {
            minIndexDate = current;
          }
        }

        const changeSeries = changeMap.get(regionId);
        if (showChange && changeSeries?.points?.length) {
          changeSeries.points.forEach((point) => {
            const value = Number(point?.[1]);
            if (!Number.isNaN(value)) {
              maxChangeAbs = Math.max(maxChangeAbs, Math.abs(value));
            }
          });
          seriesList.push({
            name: `${series.regionNameKo} 증감`,
            type: 'line',
            yAxisIndex: 1,
            smooth: false,
            sampling: 'lttb',
            connectNulls: true,
            symbol: 'none',
            color,
            lineStyle: {
              width: 2.2,
              type: 'solid',
              opacity: 1
            },
            emphasis: { focus: 'series' },
            data: changeSeries.points,
            z: 1
          });
        }
      });
    }

    return {
      animation: true,
      animationDuration: 600,
      animationEasing: 'cubicOut',
      grid: {
        left: 60,
        right: 60,
        top: 60,
        bottom: 84,
        containLabel: true
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'cross',
          label: {
            backgroundColor: '#6a7985'
          }
        },
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        borderColor: '#ddd',
        borderWidth: 1,
        textStyle: {
          color: '#333'
        },
        formatter: (params) => {
          if (!Array.isArray(params) || params.length === 0) return '';

          const date = params[0].axisValue;
          const formattedDate = new Date(date).toLocaleDateString('ko-KR', {
            year: 'numeric',
            month: 'long'
          });

          let content = `<div style="font-weight: 700; margin-bottom: 8px;">${formattedDate}</div>`;
          const metricLabel = currentIndexType === 'BUYER_SENTIMENT'
            ? buyerMetric.label
            : (mainChartMode === 'INDEX' ? '지수' : '증감');
          params.forEach((param) => {
            const rawName = param.seriesName || '';
            const displayName = (currentIndexType !== 'BUYER_SENTIMENT' && mainChartMode === 'CHANGE')
              ? rawName.replace(/ 증감$/, '')
              : rawName;
            const value = param.value?.[1];
            if (value == null) return;
            const sellerValue = param.value?.[2];
            const buyerValue = param.value?.[3];
            const indexValue = param.value?.[4];
            content += `<div style="margin: 4px 0 2px;">
              <span style="display: inline-block; width: 10px; height: 10px; border-radius: 50%; background: ${param.color}; margin-right: 8px;"></span>
              <span>${displayName} ${metricLabel}: <strong>${Number(value).toFixed(2)}</strong></span>
            </div>`;
            if (currentIndexType === 'BUYER_SENTIMENT') {
              content += `<div style="margin: 0 0 8px 18px; font-size: 12px; color: #6b7280;">
                매도자 많음 ${sellerValue != null ? Number(sellerValue).toFixed(2) : '-'} | 매수자 많음 ${buyerValue != null ? Number(buyerValue).toFixed(2) : '-'} | 매수우위지수 ${indexValue != null ? Number(indexValue).toFixed(2) : '-'}
              </div>`;
            }
          });

          return content;
        }
      },
      legend: {
        top: 10,
        left: 140,
        type: 'scroll',
        textStyle: {
          fontSize: 12
        }
      },
      xAxis: {
        type: 'time',
        boundaryGap: false,
        min: minIndexDate == null ? undefined : minIndexDate,
        axisLabel: {
          formatter: formatDate,
          fontSize: 11,
          color: '#666'
        },
        axisLine: {
          lineStyle: { color: '#ddd' }
        }
      },
      dataZoom: [
        {
          type: 'inside',
          xAxisIndex: 0,
          // 매수심리는 클라이언트 줌(zoomSpanPercent) 유지, 지수/증감은 서버가 구간을 스코프하므로 전체 표시
          start: currentIndexType === 'BUYER_SENTIMENT' ? Math.max(0, 100 - zoomSpanPercent) : 0,
          end: 100,
          // 보이는 구간 밖 데이터를 걸러 y축이 해당 구간의 최소/최대로 자동 스케일되게 함
          filterMode: 'filter',
          zoomOnMouseWheel: true,
          moveOnMouseMove: true,
          moveOnMouseWheel: true
        }
      ],
      yAxis: [
        {
          type: 'value',
          // 지수(가격) 축: 0 기준이 아니라 "현재 보이는 구간"의 데이터 최소/최대에 맞춰 스케일 → 소수점 변화가 잘 보임
          scale: true,
          axisLabel: {
            fontSize: 11,
            color: '#666'
          },
          splitLine: {
            lineStyle: { color: '#f0f0f0' }
          }
        },
        {
          type: 'value',
          position: 'right',
          min: showChange && currentIndexType !== 'BUYER_SENTIMENT' && maxChangeAbs > 0
            ? -Math.ceil(maxChangeAbs * 1.2 * 100) / 100
            : undefined,
          max: showChange && currentIndexType !== 'BUYER_SENTIMENT' && maxChangeAbs > 0
            ? Math.ceil(maxChangeAbs * 1.2 * 100) / 100
            : undefined,
          axisLabel: {
            fontSize: 10,
            color: '#4b5563'
          },
          axisLine: {
            show: true,
            lineStyle: {
              color: '#94a3b8'
            }
          },
          splitLine: {
            show: showChange && currentIndexType !== 'BUYER_SENTIMENT',
            lineStyle: {
              color: '#e5e7eb',
              type: 'dashed'
            }
          }
        }
      ],
      series: seriesList
    };
  }, [chartData, sentimentData, currentIndexType, mainChartMode, buyerSentimentMetric, zoomSpanPercent, regions]);

  // 그룹(최상위 조상 단위) 차트 — 그룹 내 여러 지역을 라인으로 합쳐서 표시.
  //  · 단일 지역: home "주간 매매가격지수" 스타일(지수 라인 + 상승/하락 증감 바)
  //  · 다지역: 지역별 지수 라인만 (색 구분 + 끝 라벨)
  const getGroupChartOption = (regionIds, groupName, groupKey) => {
    const seriesList = [];
    const single = regionIds.length === 1;

    if (currentIndexType === 'BUYER_SENTIMENT') {
      // 상단 버튼(전체/매도자많음/매수자많음/매수우위지수)로 표시할 지표 선택
      const showAll = buyerSentimentMetric === 'ALL';
      const showSeller = showAll || buyerSentimentMetric === 'SELLER';
      const showBuyer = showAll || buyerSentimentMetric === 'BUYER';
      const showIndex = showAll || buyerSentimentMetric === 'INDEX';
      if (single) {
        // 단일 지역: 선택된 지표만(전체면 3라인)
        const data = sentimentData[regionIds[0]];
        if (!Array.isArray(data)) return null;
        const dateMap = new Map();
        data.forEach(row => {
          const baseDate = row?.baseDate;
          if (!baseDate) return;
          if (!dateMap.has(baseDate)) dateMap.set(baseDate, { seller: null, buyer: null, index: null });
          const current = dateMap.get(baseDate);
          if (row.indexType === 'BUYER_SE_SELLER') current.seller = row.value;
          if (row.indexType === 'BUYER_SE_BUYER') current.buyer = row.value;
          if (row.indexType === 'BUYER_SE_INDEX') current.index = row.value;
        });
        const sortedData = Array.from(dateMap.entries()).sort((a, b) => new Date(a[0]) - new Date(b[0]));
        // 매수우위지수는 스케일이 달라 보조축(yAxisIndex 1) 사용. 단, 매수우위지수 단독이면 주축으로.
        const indexYAxis = showAll ? 1 : 0;
        if (showSeller) seriesList.push({ name: '매도자 많음', type: 'line', smooth: false, sampling: 'lttb', connectNulls: true, symbol: 'none', color: '#f59e0b', lineStyle: { width: 1.8 }, areaStyle: { color: 'rgba(245, 158, 11, 0.10)' }, endLabel: getSeriesEndLabel('매도자 많음', '#f59e0b'), labelLayout: { moveOverlap: 'shiftY' }, data: sortedData.map(([d, v]) => [d, v.seller]), z: 3 });
        if (showBuyer) seriesList.push({ name: '매수자 많음', type: 'line', smooth: false, sampling: 'lttb', connectNulls: true, symbol: 'none', color: '#3b82f6', lineStyle: { width: 1.8 }, areaStyle: { color: 'rgba(59, 130, 246, 0.10)' }, endLabel: getSeriesEndLabel('매수자 많음', '#3b82f6'), labelLayout: { moveOverlap: 'shiftY' }, data: sortedData.map(([d, v]) => [d, v.buyer]), z: 3 });
        if (showIndex) seriesList.push({ name: '매수우위지수', type: 'line', smooth: false, sampling: 'lttb', connectNulls: true, yAxisIndex: indexYAxis, symbol: 'none', color: '#10b981', lineStyle: { width: 2.4 }, endLabel: getSeriesEndLabel('매수우위지수', '#10b981'), labelLayout: { moveOverlap: 'shiftY' }, data: sortedData.map(([d, v]) => [d, v.index]), z: 4 });
      } else {
        // 다지역: 지역별 1라인. 선택 지표(전체는 매수우위지수)로 표시
        const idxType = buyerSentimentMetric === 'SELLER' ? 'BUYER_SE_SELLER'
          : buyerSentimentMetric === 'BUYER' ? 'BUYER_SE_BUYER'
            : 'BUYER_SE_INDEX';
        regionIds.forEach((rid, idx) => {
          const data = sentimentData[rid];
          if (!Array.isArray(data)) return;
          const nm = regions.find(r => r.regionId === rid)?.name || String(rid);
          const color = CHART_COLORS[idx % CHART_COLORS.length];
          const pts = data
            .filter(row => row?.indexType === idxType && row?.baseDate)
            .sort((a, b) => new Date(a.baseDate) - new Date(b.baseDate))
            .map(row => [row.baseDate, row.value]);
          seriesList.push({ name: nm, type: 'line', smooth: false, sampling: 'lttb', connectNulls: true, symbol: 'none', color, lineStyle: { width: 2 }, endLabel: getSeriesEndLabel(nm, color), labelLayout: { moveOverlap: 'shiftY' }, data: pts, z: 3 });
        });
      }
    } else {
      // 증감(CHANGE) 모드면 라인 값을 증감 데이터로, 지수(INDEX) 모드면 지수 데이터로 그린다(표기형식은 동일).
      const useChange = mainChartMode === 'CHANGE';
      const sourceSeries = useChange ? chartData?.change?.series : chartData?.index?.series;
      if (!sourceSeries) return null;
      // 지역별 라인 (지수 모드=지수값 / 증감 모드=증감값)
      regionIds.forEach((rid, idx) => {
        const s = sourceSeries.find(x => x.regionId === rid);
        if (!s) return;
        const color = CHART_COLORS[idx % CHART_COLORS.length];
        seriesList.push({ name: s.regionNameKo, type: 'line', smooth: false, sampling: 'lttb', connectNulls: true, symbol: 'none', color, lineStyle: { width: 2 }, endLabel: getSeriesEndLabel(s.regionNameKo, color), labelLayout: { moveOverlap: 'shiftY' }, data: s.data?.map(point => [point.baseDate, point.value]) || [], z: 3 });
      });
      // 지수 모드의 단일 지역에서만 상승/하락 증감 바 (증감 모드는 라인 자체가 증감값이라 생략)
      if (!useChange && single && chartData?.change?.series) {
        const cs = chartData.change.series.find(x => x.regionId === regionIds[0]);
        if (cs) {
          const pts = cs.data?.map(p => [p.baseDate, p.value]) || [];
          seriesList.push({ name: '상승', type: 'bar', yAxisIndex: 1, barMaxWidth: 10, itemStyle: { color: '#ec8b86', borderColor: '#ffffff', borderWidth: 1 }, data: pts.map(([d, v]) => [d, (v != null && v >= 0) ? v : null]), z: 1 });
          seriesList.push({ name: '하락', type: 'bar', yAxisIndex: 1, barMaxWidth: 10, itemStyle: { color: '#6aa3f4', borderColor: '#ffffff', borderWidth: 1 }, data: pts.map(([d, v]) => [d, (v != null && v < 0) ? v : null]), z: 1 });
        }
      }
    }

    if (seriesList.length === 0) return null;

    // 표시 창 계산: 차트별 수동 줌(groupZoomByKey) 우선, 없으면 최근 viewYears년(데이터는 전체 로드).
    let winStart;
    let winEnd;
    const saved = groupKey != null ? groupZoomByKey[groupKey] : null;
    if (saved) {
      winStart = saved.startValue;
      winEnd = saved.endValue;
    } else {
      let dmin = Infinity;
      let dmax = -Infinity;
      seriesList.forEach((s) => (s.data || []).forEach((pt) => {
        const t = new Date(Array.isArray(pt) ? pt[0] : pt).getTime();
        if (Number.isFinite(t)) { if (t < dmin) dmin = t; if (t > dmax) dmax = t; }
      }));
      if (Number.isFinite(dmin) && Number.isFinite(dmax) && dmax > dmin) {
        winEnd = dmax;
        const spanMs = viewYears >= FULL_VIEW_YEARS ? (dmax - dmin) : viewYears * 365 * 24 * 60 * 60 * 1000;
        winStart = Math.max(dmin, dmax - spanMs);
      }
    }

    return {
      animation: true,
      animationDuration: 600,
      animationEasing: 'cubicOut',
      grid: {
        left: 60,
        right: 120,
        top: 20,
        bottom: 24,
        containLabel: true
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'cross',
          label: {
            backgroundColor: '#6a7985'
          }
        },
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        borderColor: '#ddd',
        borderWidth: 1,
        textStyle: {
          color: '#333'
        },
        formatter: (params) => {
          if (!Array.isArray(params) || params.length === 0) return '';

          const date = params[0].axisValue;
          const formattedDate = new Date(date).toLocaleDateString('ko-KR', {
            year: 'numeric',
            month: 'long'
          });

          let content = `<div style="font-weight: 700; margin-bottom: 8px;">${formattedDate}</div>`;
          // 값 내림차순 정렬
          const sortedParams = params
            .filter(p => p.value?.[1] != null)
            .sort((a, b) => Number(b.value?.[1]) - Number(a.value?.[1]));
          sortedParams.forEach(param => {
            const value = param.value?.[1];
            content += `<div style="display: flex; align-items: center; margin: 4px 0;">
                <span style="display: inline-block; width: 10px; height: 10px; border-radius: 50%; background: ${param.color}; margin-right: 8px;"></span>
                <span>${param.seriesName}: <strong>${Number(value).toFixed(2)}</strong></span>
              </div>`;
          });

          return content;
        }
      },
      xAxis: {
        type: 'time',
        boundaryGap: false,
        axisLabel: {
          // 매수심리는 연월일까지 표기, 그 외는 연월
          formatter: currentIndexType === 'BUYER_SENTIMENT' ? formatDateFull : formatDate,
          fontSize: 11,
          color: '#666'
        },
        axisLine: {
          lineStyle: { color: '#ddd' }
        }
      },
      dataZoom: [
        {
          type: 'inside',
          xAxisIndex: 0,
          // 데이터는 전체 로드하고 화면엔 이 창(수동 줌 또는 최근 viewYears년)만 표시
          ...(winStart != null && winEnd != null ? { startValue: winStart, endValue: winEnd } : { start: 0, end: 100 }),
          // 보이는 구간 밖 데이터를 걸러 y축이 해당 구간의 최소/최대로 자동 스케일되게 함
          filterMode: 'filter',
          zoomOnMouseWheel: 'ctrl', // Ctrl+휠 = 확대/축소
          moveOnMouseMove: true,    // 드래그 = 기간 좌·우 이동
          moveOnMouseWheel: false   // 일반 휠은 페이지 스크롤
        }
      ],
      yAxis: [
        {
          type: 'value',
          // 메인 차트와 동일하게 현재 보이는 구간의 데이터 최소/최대에 맞춰 스케일
          scale: true,
          axisLabel: {
            fontSize: 11,
            color: '#666'
          },
          splitLine: {
            lineStyle: { color: '#f0f0f0' }
          }
        },
        {
          type: 'value',
          position: 'right',
          axisLabel: {
            fontSize: 10,
            color: '#9ca3af'
          },
          splitLine: {
            show: false
          }
        }
      ],
      series: seriesList
    };
  };

  // kb_region 계층(parentId/sortOrder) → 트리. 계층 정보가 없으면 자동으로 평면 목록이 됨.
  const regionTree = useMemo(() => {
    const byId = new Map((regions || []).map((r) => [r.regionId, { ...r, children: [] }]));
    const roots = [];
    byId.forEach((node) => {
      const parent = node.parentId != null && byId.has(node.parentId) ? byId.get(node.parentId) : null;
      if (parent && parent !== node) parent.children.push(node);
      else roots.push(node);
    });
    const sortNodes = (arr) => {
      arr.sort((a, b) => (a.sortOrder ?? 9999) - (b.sortOrder ?? 9999) || a.regionId - b.regionId);
      arr.forEach((n) => sortNodes(n.children));
    };
    sortNodes(roots);
    return roots;
  }, [regions]);

  // 선택 지역을 "최상위 조상" 기준으로 그룹핑 → 그룹당 차트 1개(부산+구들 → 부산 차트 하나)
  const chartGroups = useMemo(() => {
    const byId = new Map((regions || []).map((r) => [r.regionId, r]));
    const topAncestorId = (id) => {
      let cur = byId.get(id);
      let guard = 0;
      while (cur && cur.parentId != null && byId.has(cur.parentId) && guard < 10) {
        cur = byId.get(cur.parentId);
        guard += 1;
      }
      return cur ? cur.regionId : id;
    };
    const order = [];
    const map = new Map();
    selectedRegions.forEach((id) => {
      const key = topAncestorId(id);
      if (!map.has(key)) { map.set(key, []); order.push(key); }
      map.get(key).push(id);
    });
    return order.map((key) => ({
      ancestorId: key,
      regionIds: map.get(key),
      groupName: byId.get(key)?.name || String(key),
    }));
  }, [selectedRegions, regions]);

  // 노드 + 모든 하위의 regionId 수집
  const collectSubtreeIds = (node) => {
    const ids = [node.regionId];
    node.children.forEach((c) => ids.push(...collectSubtreeIds(c)));
    return ids;
  };

  // 체크: 부모를 켜면/끄면 하위 전체 일괄 선택/해제 (리프는 자기 자신만)
  const handleRegionCheck = (node, checked) => {
    const ids = collectSubtreeIds(node);
    setSelectedRegions((prev) => {
      const set = new Set(prev);
      if (checked) ids.forEach((id) => set.add(id));
      else ids.forEach((id) => set.delete(id));
      return Array.from(set);
    });
  };

  const selectedRegionSet = new Set(selectedRegions);

  const renderRegionNode = (node, depth = 0) => {
    const hasChildren = node.children.length > 0;
    const isExpanded = !!expandedRegions[node.regionId];
    const subtreeIds = collectSubtreeIds(node);
    const selCount = subtreeIds.reduce((n, id) => n + (selectedRegionSet.has(id) ? 1 : 0), 0);
    const allSelected = selCount === subtreeIds.length;
    const someSelected = selCount > 0 && !allSelected; // 하위 일부만 선택 → indeterminate
    return (
      <React.Fragment key={node.regionId}>
        <div className="kb-region-row" style={{ paddingLeft: 2 + depth * 14 }}>
          {hasChildren ? (
            <button
              type="button"
              className="kb-region-toggle"
              onClick={() => setExpandedRegions((p) => ({ ...p, [node.regionId]: !isExpanded }))}
              aria-label={isExpanded ? '접기' : '펼치기'}
            >
              {isExpanded ? '−' : '+'}
            </button>
          ) : (
            <span className="kb-region-toggle-spacer" />
          )}
          <label className="kb-region-item">
            <input
              type="checkbox"
              checked={allSelected}
              ref={(el) => { if (el) el.indeterminate = someSelected; }}
              onChange={(e) => handleRegionCheck(node, e.target.checked)}
            />
            <span>{node.name}</span>
          </label>
        </div>
        {hasChildren && isExpanded && node.children.map((child) => renderRegionNode(child, depth + 1))}
      </React.Fragment>
    );
  };

  // 차트 하단 KB지수-표 (KB지수-표 메뉴와 동일한 구조/색상 규칙 유지)
  const renderCompareTable = () => {
    if (selectedRegions.length === 0) {
      return <div className="kb-compare-table-empty">지역을 선택하면 표가 표시됩니다.</div>;
    }
    if (tableLoading && !tableData) {
      return <div className="kb-compare-table-empty">표 데이터를 불러오는 중...</div>;
    }
    if (!tableData || !Array.isArray(tableData.data) || tableData.data.length === 0) {
      return <div className="kb-compare-table-empty">표 데이터가 없습니다.</div>;
    }

    const headers = Array.isArray(tableData.headers) ? tableData.headers : [];
    const subHeaders = Array.isArray(tableData.subHeaders) ? tableData.subHeaders : null;
    const rows = tableData.data;
    const tType = tableData._indexType;
    const isWeeklyChangeTable = tType === 'SALE' || tType === 'JEONSE';

    if (headers.length === 0) {
      return <div className="kb-compare-table-empty">헤더 정보가 없습니다.</div>;
    }

    // 매수매도심리 표 (2행 헤더: 지역 > 매도자많음/매수자많음/매수우위지수)
    if (subHeaders && subHeaders.length > 0) {
      return (
        <>
        <div className="kb-change-guide kb-sentiment-legend">
          <span className="kb-sentiment-legend-label">타이밍 기준 색상:</span>
          {(timingConfig && timingConfig.length ? timingConfig : DEFAULT_TIMING_CONFIG).map((item) => (
            <span key={item.category || item.label} className="kb-sentiment-legend-item">
              <span className="kb-sentiment-legend-swatch" style={{ backgroundColor: item.color }} />
              {item.label}
            </span>
          ))}
        </div>
        <div className="kb-table-container kb-compare-table-container">
          <table className="kb-table buyer-sentiment-table kb-table-bordered">
            <thead>
              <tr>
                <th className="sticky-col" rowSpan="2">날짜</th>
                {headers.map((region, index) => (
                  <th key={index} colSpan={subHeaders.length}>{region}</th>
                ))}
              </tr>
              <tr>
                {headers.map((region, regionIndex) => (
                  subHeaders.map((subHeader, subIndex) => (
                    <th key={`${regionIndex}-${subIndex}`} className="sub-header">{subHeader}</th>
                  ))
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, rowIndex) => (
                <tr key={rowIndex}>
                  <td className="sticky-col">{row.baseDate || '-'}</td>
                  {headers.map((region, regionIndex) => {
                    // 지역별 (매도세/매수세/매수우위) 조합으로 타이밍 색상 판정 → 해당 지역 3칸에 동일 배경색
                    const timingColor = getSentimentTimingColor(
                      row.values?.seller?.[region],
                      row.values?.buyer?.[region],
                      row.values?.index?.[region],
                      timingConfig
                    );
                    return subHeaders.map((subHeader, subIndex) => {
                      const key = subHeader === '매도자많음' ? 'seller'
                        : subHeader === '매수자많음' ? 'buyer'
                          : 'index';
                      const value = row.values?.[key]?.[region];
                      return (
                        <td key={`${regionIndex}-${subIndex}`} style={timingColor ? { backgroundColor: timingColor } : undefined}>
                          {value !== null && value !== undefined ? Number(value).toFixed(2) : '-'}
                        </td>
                      );
                    });
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        </>
      );
    }

    // 일반 지수/증감 표. 지역 선택 구성에 따라 상위/하위 그룹핑이 불안정해(빈칸 발생)
    // 각 지역을 하나의 회색 헤더 셀로 통일. sortOrder DESC → 최신이 위, 전주는 아래(rowIndex+1)
    return (
      <>
        {isWeeklyChangeTable && (
          <div className="kb-change-guide">
            전 주 데이터 대비 음영 기준: ±0.20%, ±0.40%, ±0.60%, ±0.80% 이상 구간별 상승/하락 표시
          </div>
        )}
        <div className="kb-table-container kb-compare-table-container">
          <table className="kb-table kb-table-bordered">
            <thead>
              <tr>
                <th className="sticky-col">날짜</th>
                {headers.map((region, index) => (
                  <th key={`header-${index}`} className="region-header">
                    {region}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, rowIndex) => (
                <tr key={rowIndex}>
                  <td className="sticky-col">{row.baseDate || '-'}</td>
                  {headers.map((region, colIndex) => {
                    const value = row.values?.[region];
                    const prevWeekIndex = rowIndex + 1; // DESC: 전주는 아래쪽
                    const prevValue = (prevWeekIndex >= 0 && prevWeekIndex < rows.length)
                      ? rows[prevWeekIndex]?.values?.[region]
                      : null;
                    const delta =
                      value !== null && value !== undefined && prevValue !== null && prevValue !== undefined
                        ? Number(value) - Number(prevValue)
                        : null;
                    return (
                      <td key={colIndex} className={getWeeklyChangeClassByDelta(delta, isWeeklyChangeTable)}>
                        {value !== null && value !== undefined ? Number(value).toFixed(2) : '-'}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </>
    );
  };

  return (
    <div className="kb-region-compare">
      <div className="kb-layout">
        <aside className="kb-region-sidebar">
          <div className="kb-region-sidebar-head">
            <strong>지역</strong>
            <div className="kb-region-sidebar-actions">
              <button
                type="button"
                className="kb-region-sel-btn"
                onClick={handleSelectAllRegions}
                disabled={selectedRegions.length === regions.length}
              >
                전체선택
              </button>
              <button
                type="button"
                className="kb-region-sel-btn"
                onClick={handleDeselectAllRegions}
                disabled={selectedRegions.length === 0}
              >
                전체해제
              </button>
            </div>
          </div>
          <div className="kb-region-sidebar-count">{selectedRegions.length} / {regions.length} 선택</div>
          <div className="kb-region-sidebar-list">
            {regionTree.map((node) => renderRegionNode(node, 0))}
          </div>
        </aside>

        <div className="kb-main">
      <div className="kb-chart-section">
        {loading && <div className="loading">데이터를 불러오는 중...</div>}
        {error && <div className="error">{error}</div>}
        {!loading && !error && (
          <>
            <div className="kb-chart-wrapper">
              <div className="kb-chart-topbar">
                <div className="kb-chart-toggle">
                  {INDEX_TYPES.map(type => (
                    <button
                      key={type.value}
                      className={`kb-toggle-btn ${currentIndexType === type.value ? 'active' : ''}`}
                      onClick={() => setCurrentIndexType(type.value)}
                    >
                      {type.label}
                    </button>
                  ))}
                </div>
                <div className="kb-main-mode-toggle">
                  {currentIndexType === 'BUYER_SENTIMENT' ? (
                    BUYER_SENTIMENT_METRICS.map(mode => (
                      <button
                        key={mode.value}
                        className={`kb-toggle-btn kb-toggle-btn-sm ${buyerSentimentMetric === mode.value ? 'active' : ''}`}
                        onClick={() => setBuyerSentimentMetric(mode.value)}
                      >
                        {mode.label}
                      </button>
                    ))
                  ) : (
                    MAIN_CHART_MODES.map(mode => (
                      <button
                        key={mode.value}
                        className={`kb-toggle-btn kb-toggle-btn-sm ${mainChartMode === mode.value ? 'active' : ''}`}
                        onClick={() => setMainChartMode(mode.value)}
                      >
                        {mode.label}
                      </button>
                    ))
                  )}
                </div>
                {/* 전역 줌 컨트롤 (모든 차트 공통 적용) */}
                <div className="kb-zoom-controls">
                  <button type="button" className="kb-toggle-btn kb-toggle-btn-sm" onClick={handleChartZoomOut} title="기간 넓히기">-</button>
                  <button type="button" className="kb-toggle-btn kb-toggle-btn-sm" onClick={handleChartZoomIn} title="기간 좁히기">+</button>
                  <button type="button" className="kb-toggle-btn kb-toggle-btn-sm" onClick={handleChartZoomReset} title="전체 기간">전체</button>
                  <span className="kb-zoom-hint" style={{ fontSize: 11, color: '#94a3b8', marginLeft: 8, whiteSpace: 'nowrap' }}>
                    Ctrl+휠 확대/축소 · 드래그 기간이동
                  </span>
                </div>
              </div>
            </div>

            {selectedRegions.length > 0 && (
              <div className="kb-individual-charts">
                {chartGroups.map(({ ancestorId, regionIds, groupName }) => {
                  const option = getGroupChartOption(regionIds, groupName, ancestorId);
                  if (!option) return null;
                  const isSentiment = currentIndexType === 'BUYER_SENTIMENT';
                  // 모든 차트를 1줄에 하나씩(전체 폭)으로 크게 표시
                  const wide = true;
                  const chartHeight = isSentiment ? '360px' : '320px';

                  return (
                    <div key={ancestorId} className={`kb-individual-chart ${wide ? 'kb-individual-chart--wide' : ''}`}>
                      <div className="kb-individual-chart-head">
                        <span className="kb-individual-chart-title">
                          {groupName}
                          {regionIds.length > 1 && <span className="kb-chart-subcount"> · {regionIds.length}개 지역</span>}
                        </span>
                      </div>
                      <ReactECharts
                        ref={(el) => { if (el) groupChartRefs.current[ancestorId] = el; else delete groupChartRefs.current[ancestorId]; }}
                        option={option}
                        style={{ width: '100%', height: chartHeight }}
                        opts={{ renderer: 'canvas' }}
                        notMerge
                        lazyUpdate
                        onEvents={{ datazoom: () => captureGroupZoom(ancestorId) }}
                      />
                    </div>
                  );
                })}
              </div>
            )}

            {/* 화면 맨 아래: 선택 지역 전체를 하나로 합친 KB지수-표 (증감 뷰에서 색상 규칙 유지) */}
            <div className="kb-compare-table-block">
              <div className="kb-compare-table-title">
                {currentIndexType === 'BUYER_SENTIMENT'
                  ? '매수매도심리 표'
                  : `${INDEX_TYPES.find(t => t.value === currentIndexType)?.label || ''} ${mainChartMode === 'CHANGE' ? '증감 표' : '표'}`}
                {tableLoading && <span className="kb-compare-table-loading"> · 갱신 중...</span>}
              </div>
              {renderCompareTable()}
            </div>
          </>
        )}
      </div>
        </div>
      </div>
    </div>
  );
}

export default KbRegionCompare;
